import { useEffect, useState } from 'react'
import { AlignLeft, Box, Component, Link2, SlidersHorizontal } from 'lucide-react'
import type {
  CanvasNode,
  DesignTokens,
  StyleMap,
  StyleValue,
} from '../core/model'

interface InspectorPanelProps {
  node: CanvasNode
  tokens: DesignTokens
  onUpdateNode: (
    patch: Partial<Pick<CanvasNode, 'text' | 'styles' | 'name'>>,
    label?: string,
  ) => void
  onUpdateToken: (token: keyof DesignTokens, value: string) => void
}

interface CommitInputProps {
  value: string
  ariaLabel: string
  onCommit: (value: string) => void
  multiline?: boolean
}

function CommitInput({
  value,
  ariaLabel,
  onCommit,
  multiline = false,
}: CommitInputProps) {
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value])
  const commit = () => {
    const next = draft.trim()
    if (next !== value) onCommit(next)
  }

  return multiline ? (
    <textarea
      value={draft}
      aria-label={ariaLabel}
      rows={3}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
    />
  ) : (
    <input
      value={draft}
      aria-label={ariaLabel}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur()
      }}
    />
  )
}

function StyleField({
  property,
  value,
  onCommit,
}: {
  property: string
  value: StyleValue | undefined
  onCommit: (styles: StyleMap) => void
}) {
  return (
    <label className="property-field">
      <span>{property.replace(/([A-Z])/g, ' $1')}</span>
      <CommitInput
        value={value === undefined ? '—' : String(value)}
        ariaLabel={property}
        onCommit={(nextValue) => onCommit({ [property]: nextValue })}
      />
    </label>
  )
}

const tokenLabels: Record<keyof DesignTokens, string> = {
  accent: 'Signal',
  background: 'Canvas',
  surface: 'Surface',
  ink: 'Ink',
  muted: 'Muted',
  accentInk: 'On signal',
  border: 'Border',
  radius: 'Radius',
  font: 'Typeface',
}

function colorInputValue(value: string): string {
  return /^#[0-9a-f]{6}$/i.test(value) ? value : '#737780'
}

export function InspectorPanel({
  node,
  tokens,
  onUpdateNode,
  onUpdateToken,
}: InspectorPanelProps) {
  const styleProperties = ['fontSize', 'fontWeight', 'padding', 'borderRadius']

  return (
    <div className="inspector-content">
      <section className="inspector-section selection-summary">
        <div className="selection-icon"><Component size={14} /></div>
        <div>
          <strong>{node.name}</strong>
          <span>
            &lt;{node.tag}&gt; · {node.kind}
          </span>
        </div>
        <Link2 size={13} className="bound-icon" aria-label="Bound to code" />
      </section>

      {node.text !== undefined ? (
        <section className="inspector-section">
          <div className="section-title">
            <AlignLeft size={12} /> Content
          </div>
          <label className="wide-field">
            <span>Text</span>
            <CommitInput
              value={node.text}
              ariaLabel="Node text content"
              multiline
              onCommit={(text) => onUpdateNode({ text }, 'Edit text content')}
            />
          </label>
        </section>
      ) : null}

      <section className="inspector-section">
        <div className="section-title">
          <Box size={12} /> Layout & type
        </div>
        <div className="property-grid">
          {styleProperties.map((property) => (
            <StyleField
              key={property}
              property={property}
              value={node.styles[property]}
              onCommit={(styles) =>
                onUpdateNode({ styles }, `Change ${property}`)
              }
            />
          ))}
        </div>
      </section>

      <section className="inspector-section">
        <div className="section-title">
          <SlidersHorizontal size={12} /> Design tokens
          <span className="token-count">9</span>
        </div>
        <div className="token-list">
          {(Object.keys(tokens) as Array<keyof DesignTokens>).map((token) => (
            <div className="token-row" key={token}>
              <input
                className="token-swatch"
                type="color"
                value={colorInputValue(tokens[token])}
                aria-label={`Change ${tokenLabels[token]} token`}
                disabled={!tokens[token].startsWith('#')}
                onChange={(event) => onUpdateToken(token, event.target.value)}
              />
              <span>{tokenLabels[token]}</span>
              <code>{tokens[token]}</code>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}
